import { hasCampaignPermission, inventoryField, layerOf } from '../shared/array-types';
import { findInventoryInstanceNode } from './find-cached-item';
import { isUuid } from './helpers';

type Row = Record<string, unknown>;
function canManageLinks(row: Row): boolean {
    return hasCampaignPermission(row) || window.FateTide?.user?.isGM === true
        || document.querySelector('[data-ft3d-can-manage="1"]') !== null;
}

function cachedEncounter(id: string): boolean {
    return [...(window.FateTide?.encounterCache?.keys() ?? [])].some(key => key.toUpperCase() === id);
}

export function inventoryEncounterLinks(row: Row): Row[] {
    return Array.isArray(row.inventory_encounter_link) ? row.inventory_encounter_link as Row[] : [];
}

/** Display only: routing, cover and destruction always consume the complete saved link set. */
export function visibleInventoryEncounterLinks(row: Row, lookup = findInventoryInstanceNode, readableRoots?: ReadonlySet<string>): Row[] {
    const links = inventoryEncounterLinks(row);
    if (canManageLinks(row)) return links;
    const ownId = String(inventoryField(row, 'inventory_id') ?? '').toUpperCase();
    return links.filter(link => Number(layerOf(link, 'encounter_link_private') ?? 0) === 0
        && ['encounter_link_from', 'encounter_link_to'].every(field => {
            let id = String(layerOf(link, field) ?? '').toUpperCase();
            const seen = new Set<string>();
            for (let depth = 0; depth < 64 && isUuid(id) && !seen.has(id); depth++) {
                seen.add(id);
                if (cachedEncounter(id) || readableRoots?.has(id)) return true;
                const node = id === ownId ? row : lookup(id);
                if (!node) return readableRoots === undefined; // Hydration resolves unheld endpoints before attaching HTML.
                if (Number(inventoryField(node, 'inventory_private') ?? 0) !== 0) return false;
                id = String(inventoryField(node, 'possess_id') ?? '').toUpperCase();
            }
            return false;
        }));
}

export interface EncounterLinkDisplayProjection { visible: ReadonlySet<Row> }

// Successful root reads need no repeated whole-inventory fetch on later card opens.
// Keep failures retryable and bind the verdicts to this page's runtime/session.
const readableRootsByPage = new WeakMap<object, Set<string>>();

/** Resolve foreign possess chains through the existing raw read surface, without changing shared caches. */
export async function resolveEncounterLinkDisplay(row: Row): Promise<EncounterLinkDisplayProjection> {
    const all = inventoryEncounterLinks(row);
    if (canManageLinks(row)) return { visible: new Set(all) };
    const page = window.FateTide;
    const roots = (page && readableRootsByPage.get(page)) ?? new Set<string>();
    if (page) readableRootsByPage.set(page, roots);
    const nodes = new Map<string, Row>(), reads = new Map<string, Promise<void>>();
    nodes.set(String(inventoryField(row, 'inventory_id') ?? '').toUpperCase(), row);
    const lookup = (id: string): Row | null => nodes.get(id) ?? findInventoryInstanceNode(id);
    const load = (id: string): Promise<void> => {
        if (!reads.has(id)) reads.set(id, (async () => {
            const baseUrl = typeof BASE_URL === 'string' ? BASE_URL : '/';
            const response = await fetch(baseUrl + 'options/api/base_entity_inc.php?entity=inventory&cold=0&entity_id=' + encodeURIComponent(id),
                { credentials: 'same-origin', signal: AbortSignal.timeout(15_000) });
            if (!response.ok) return;
            const envelope = await response.json();
            const base = envelope?.base;
            if (String(base?.entity_id ?? '').toUpperCase() !== id || !base.possessor || typeof base.possessor !== 'object') return;
            for (const node of Array.isArray(base.inventory) ? base.inventory : []) {
                const key = String(inventoryField(node, 'inventory_id') ?? '').toUpperCase();
                if (isUuid(key)) nodes.set(key, node);
            }
            const key = String(inventoryField(base.possessor, 'inventory_id') ?? '').toUpperCase();
            if (key === id) nodes.set(id, base.possessor);
            else roots.add(id);
        })().catch(() => {}));
        return reads.get(id)!;
    };
    const visit = async (value: unknown): Promise<void> => {
        let id = String(value ?? '').toUpperCase();
        const seen = new Set<string>();
        for (let depth = 0; depth < 64 && isUuid(id) && !seen.has(id); depth++) {
            seen.add(id);
            if (roots.has(id) || cachedEncounter(id)) return;
            if (!lookup(id)) await load(id);
            const node = lookup(id);
            if (!node || Number(inventoryField(node, 'inventory_private') ?? 0) !== 0) return;
            id = String(inventoryField(node, 'possess_id') ?? '').toUpperCase();
        }
    };
    await Promise.all(all.flatMap(link => ['encounter_link_from', 'encounter_link_to'].map(field => visit(layerOf(link, field)))));
    return { visible: new Set(visibleInventoryEncounterLinks(row, lookup, roots)) };
}

/** Match the retained fragment by saved link identity, independently of destination reads. */
export function projectEncounterLinkFragment(body: string, row: Row, projection?: EncounterLinkDisplayProjection): string {
    if (canManageLinks(row)) return body;
    const visible = projection?.visible ?? new Set(visibleInventoryEncounterLinks(row));
    if (!visible.size) return '';
    const root = document.createElement('div'); root.innerHTML = body;
    const list = root.querySelector('ul.list-group');
    if (!list) return '';
    const rows = [...list.children].filter(child => child.tagName === 'LI' && !child.hasAttribute('aria-current'));
    if (rows.some(element => !element.getAttribute('data-ft-encounter-link-id'))) return ''; // Old cached markup has no identity.
    const visibleIds = new Set([...visible].map(link => String(layerOf(link, 'encounter_link_id') ?? '').toUpperCase()));
    const retained = rows.filter(element => {
        if (visibleIds.has(element.getAttribute('data-ft-encounter-link-id')!.toUpperCase())) return true;
        element.remove();
        return false;
    });
    return retained.length ? root.innerHTML : '';
}
